import { useState, useEffect } from 'react';
import { supabase } from '../supabase/config';

// Helper to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  const converted = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    converted[camelKey] = obj[key];
  }
  return converted;
};

// usePortfolio - Fetch profile data
export function usePortfolio() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const { data, error: fetchError } = await supabase
          .from('portfolio')
          .select('*')
          .eq('id', 'profile')
          .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
          throw fetchError;
        }
        setProfile(toCamelCase(data));
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, []);

  return { profile, loading, error };
}

// useProjects - Fetch projects
export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const { data, error: fetchError } = await supabase
          .from('projects')
          .select('*, categories(*)')
          .eq('status', 'completed')
          .order('featured', { ascending: false })
          .order('order', { ascending: false });

        if (fetchError) throw fetchError;
        setProjects(toCamelCase(data || []));
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchProjects();
  }, []);

  return { projects, loading, error };
}

// useSkills - Fetch and group skills
export function useSkills() {
  const [skillsByCategory, setSkillsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchSkills() {
      try {
        const { data, error: fetchError } = await supabase
          .from('skills')
          .select('*')
          .order('order');

        if (fetchError) throw fetchError;

        const grouped = (data || []).reduce((acc, skill) => {
          const category = skill.category || 'Other';
          if (!acc[category]) acc[category] = [];
          acc[category].push(toCamelCase(skill));
          return acc;
        }, {});
        setSkillsByCategory(grouped);
      } catch (err) {
        console.error('Error fetching skills:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchSkills();
  }, []);

  return { skillsByCategory, loading, error };
}

// useExperience - Fetch experience
export function useExperience() {
  const [experience, setExperience] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchExperience() {
      try {
        const { data, error: fetchError } = await supabase
          .from('experience')
          .select('*')
          .order('start_date', { ascending: false });

        if (fetchError) throw fetchError;
        setExperience(toCamelCase(data || []));
      } catch (err) {
        console.error('Error fetching experience:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchExperience();
  }, []);

  return { experience, loading, error };
}

// useTestimonials - Fetch testimonials
export function useTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchTestimonials() {
      try {
        const { data, error: fetchError } = await supabase
          .from('testimonials')
          .select('*')
          .order('featured', { ascending: false })
          .order('order', { ascending: true });

        if (fetchError) throw fetchError;
        setTestimonials(toCamelCase(data || []));
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchTestimonials();
  }, []);

  return { testimonials, loading, error };
}

// useSections - Fetch section visibility (if sections table exists)
export function useSections() {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSections() {
      try {
        const { data, error } = await supabase
          .from('sections')
          .select('*')
          .order('order');

        if (error && error.code !== '42P01') { // 42P01 = table doesn't exist
          throw error;
        }

        // If sections table exists, use it
        if (data && data.length > 0) {
          const sectionsMap = {};
          data.forEach(section => {
            sectionsMap[section.name] = {
              visible: section.visible !== false,
              title: section.title || section.name,
              order: section.order || 0
            };
          });
          setSections(sectionsMap);
        } else {
          // Default: all sections visible
          setSections({
            hero: { visible: true, title: 'Hero' },
            about: { visible: true, title: 'About' },
            skills: { visible: true, title: 'Skills' },
            projects: { visible: true, title: 'Projects' },
            experience: { visible: true, title: 'Experience' },
            testimonials: { visible: true, title: 'Testimonials' },
            process: { visible: true, title: 'Process' },
            cta: { visible: true, title: 'CTA' }
          });
        }
      } catch (err) {
        console.error('Error fetching sections:', err);
        // Default: all sections visible
        setSections({
          hero: { visible: true, title: 'Hero' },
          about: { visible: true, title: 'About' },
          skills: { visible: true, title: 'Skills' },
          projects: { visible: true, title: 'Projects' },
          experience: { visible: true, title: 'Experience' },
          testimonials: { visible: true, title: 'Testimonials' },
          process: { visible: true, title: 'Process' },
          cta: { visible: true, title: 'CTA' }
        });
      } finally {
        setLoading(false);
      }
    }
    fetchSections();
  }, []);

  return { sections, loading };
}

